import { motion } from "framer-motion";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { Badge } from "./ui/badge";
import { Award, GraduationCap } from "lucide-react";

interface ExpertCardProps {
  name: string;
  title: string;
  specialties: string[];
  credentials: string;
  image: string;
  bio: string;
}

export function ExpertCard({ name, title, specialties, credentials, image, bio }: ExpertCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 group border border-gray-100 hover:border-[#d4a574]/30"
    >
      <div className="relative h-80 overflow-hidden">
        <ImageWithFallback
          src={image}
          alt={name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
        
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-5 h-5 text-[#d4a574]" />
            <span className="text-sm opacity-90">{credentials}</span>
          </div>
        </div>
      </div>
      
      <div className="p-8">
        <h3 className="text-[#2c2c2c] mb-1 group-hover:text-[#d4a574] transition-colors">{name}</h3>
        <div className="flex items-center gap-2 mb-4">
          <GraduationCap className="w-4 h-4 text-[#d4a574]" />
          <p className="text-[#d4a574]">{title}</p>
        </div>
        
        <p className="text-[#666] leading-relaxed mb-6 text-sm">{bio}</p>
        
        <div className="flex flex-wrap gap-2">
          {specialties.map((specialty, index) => (
            <Badge
              key={index}
              variant="secondary"
              className="bg-gradient-to-r from-[#f9f1e8] to-[#faf8f5] text-[#d4a574] hover:from-[#f0e8dc] hover:to-[#f9f1e8] border-0 shadow-sm"
            >
              {specialty}
            </Badge>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
